import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Zap, ShieldCheck, Gauge, Download, Star } from "lucide-react";
import { api, mediaUrl, downloadUrl } from "@/lib/api";
import { AppCard } from "@/components/AppCard";
import { InstallGuide } from "@/components/InstallGuide";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";

const CATS = ["All", "Tools", "Games", "Social", "Media", "Productivity", "Launchers", "System", "Utilities"];

export default function Store() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [guide, setGuide] = useState({ open: false, name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== "All") params.category = category;
      if (search) params.search = search;
      const { data } = await api.get("/apps", { params });
      setApps(data);
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const triggerDownload = (app, versionId) => {
    const a = document.createElement("a");
    a.href = downloadUrl(app.id, versionId);
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setGuide({ open: true, name: app.name });
  };

  const featured = apps.find((a) => a.featured) || apps[0];

  return (
    <div className="min-h-screen bg-[#08090E]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#1E2433]/80 bg-[#08090E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-8">
          <button
            data-testid="logo-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <Logo size="sm" />
          </button>
          <div className="relative ml-auto max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search apps, developers, packages…"
              className="w-full rounded-lg border border-[#1E2433] bg-[#10131B] py-2 pl-9 pr-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-emerald-500/50"
            />
          </div>
        </div>
        {/* Category pills */}
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 pb-3 sm:px-8">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`shrink-0 rounded-full border px-3.5 py-1 font-mono text-xs uppercase tracking-wider transition-all ${
                category === c
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                  : "border-[#1E2433] text-slate-400 hover:border-slate-600 hover:text-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        {/* Hero */}
        <section className="co-grid relative mb-10 overflow-hidden rounded-2xl border border-[#1E2433] bg-[#0b0d13] p-6 sm:p-10">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                <Zap className="h-3 w-3" /> crazyOne Android Engine v3.2
              </span>
              <h1 className="font-display mt-4 text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl">
                The sovereign store for <span className="text-emerald-400 co-text-glow">limitless</span> APKs.
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                Browse, install and update Android apps with one tap. Every APK is virus-checked, zero-throttle, and yours to keep.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                {[
                  { icon: ShieldCheck, t: "100% Virus-checked" },
                  { icon: Gauge, t: "Zero-throttle" },
                  { icon: Download, t: "One-tap install" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                    <f.icon className="h-4 w-4 text-emerald-400" /> {f.t}
                  </div>
                ))}
              </div>
            </div>

            {featured && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-5"
              >
                <div
                  data-testid="featured-app"
                  onClick={() => navigate(`/app/${featured.slug}`)}
                  className="group cursor-pointer rounded-xl border border-emerald-500/25 bg-[#10131B]/80 p-5 transition-all hover:border-emerald-500/60 hover:shadow-[0_0_30px_rgba(0,240,255,0.12)]"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Featured today</p>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="h-20 w-20 overflow-hidden rounded-2xl border border-[#1E2433] bg-[#0b0d13]">
                      {mediaUrl(featured.icon_path) ? (
                        <img src={mediaUrl(featured.icon_path)} alt={featured.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center font-display text-3xl font-black text-emerald-300">
                          {featured.name?.[0]}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl font-bold text-slate-100">{featured.name}</h3>
                      <p className="text-xs text-slate-500">{featured.developer}</p>
                      <div className="mt-1 flex items-center gap-2 text-xs text-amber-400">
                        <Star className="h-3 w-3 fill-amber-400" /> {featured.rating?.toFixed(1)}
                        <span className="font-mono text-slate-500">v{featured.current_version}</span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs text-slate-400">{featured.description}</p>
                  <button
                    data-testid="featured-download"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerDownload(featured);
                    }}
                    className="co-btn-primary mt-4 flex w-full items-center justify-center gap-2 rounded-lg py-2 text-sm"
                  >
                    <Download className="h-4 w-4" /> Install now
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </section>

        {/* Grid */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold text-slate-100">
            {category === "All" ? "All apps" : category}
          </h2>
          <span className="font-mono text-xs text-slate-500">{apps.length} apps</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-44 animate-pulse rounded-xl border border-[#1E2433] bg-[#10131B]" />
            ))}
          </div>
        ) : apps.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#1E2433] py-20 text-center">
            <p className="font-display text-xl text-slate-400">No apps found</p>
            <p className="mt-1 text-sm text-slate-600">Try a different category or search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {apps.map((app, i) => (
              <AppCard
                key={app.id}
                app={app}
                index={i}
                onOpen={(a) => navigate(`/app/${a.slug}`)}
                onDownload={triggerDownload}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />

      <InstallGuide open={guide.open} appName={guide.name} onClose={() => setGuide({ open: false, name: "" })} />
    </div>
  );
}
